/**
 * <burnout-marquee>
 *
 * Seamless infinite ticker.
 *
 * Clones its single content set once so the track can translate exactly -50%
 * and land back where it started, with no visible seam. The clone is inert and
 * aria-hidden, so screen readers and the tab order only ever see the original.
 *
 * Duration is derived from the measured width against a speed in pixels per
 * second, which keeps the scroll rate identical whether there are three claims
 * or six — a fixed duration would make a longer strip race.
 */
class BurnoutMarquee extends HTMLElement {
  #track = null;
  #set = null;

  connectedCallback() {
    this.#track = this.querySelector('.bo-mq__track');
    this.#set = this.querySelector('.bo-mq__set');
    if (!this.#track || !this.#set) return;

    const clone = this.#set.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    this.#track.appendChild(clone);

    this.#measure();

    // Widths change with the viewport and once webfonts land, so re-derive
    // rather than trusting the first paint.
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => this.#measure()).observe(this.#set);
    }
    document.fonts?.ready.then(() => this.#measure());
  }

  #measure = () => {
    const width = this.#set.scrollWidth;
    if (!width) return;
    const speed = Number(this.dataset.speed) || 60; // px per second
    this.style.setProperty('--bo-mq-duration', `${width / speed}s`);
  };
}

if (!customElements.get('burnout-marquee')) {
  customElements.define('burnout-marquee', BurnoutMarquee);
}
